import { useEffect, useId, useState } from "react";
import { Checkbox } from "@/components/ui/shadcn/checkbox";
import { useShow } from "@/contexts/ShowContext";
import type { AssetCategory } from "@/types";

interface TagsEditorProps {
  assetCategories: AssetCategory[];
  assets: any[];
  onSaved?: () => void;
  onCancel?: () => void;
  showActions?: boolean;
  cta?: string;
}

export const TagsEditor = ({
  assetCategories,
  assets,
  onSaved,
  onCancel,
  showActions = true,
  cta = "Save",
}: TagsEditorProps) => {
  /**
   * Deps
   */

  const editorId = useId();

  /**
   * State
   */

  const { handleSaveTags, tagSavingAssetIds } = useShow();
  const [touchedTagIds, setTouchedTagIds] = useState<string[]>([]);
  const [checkedTagIds, setCheckedTagIds] = useState<string[]>([]);

  const saving = assets.some((asset: any) => tagSavingAssetIds.has(asset._id));

  /**
   * Functions
   */

  const tagCheckState = (tagId: string): boolean | "indeterminate" => {
    if (touchedTagIds.includes(tagId)) return checkedTagIds.includes(tagId);
    const withTag = assets.filter((asset: any) =>
      (asset?.tags || []).includes(tagId),
    ).length;
    if (withTag === 0) return false;
    if (withTag === assets.length) return true;
    return "indeterminate";
  };

  const toggleTag = (tagId: string, checked: boolean) => {
    setTouchedTagIds((prev) =>
      prev.includes(tagId) ? prev : [...prev, tagId],
    );
    setCheckedTagIds((prev) =>
      checked
        ? prev.includes(tagId)
          ? prev
          : [...prev, tagId]
        : prev.filter((id) => id !== tagId),
    );
  };

  const selectAll = () => {
    const allIds = assetCategories.map((tag) => tag._id);
    setTouchedTagIds(allIds);
    setCheckedTagIds(allIds);
  };

  const unselectAll = () => {
    setTouchedTagIds(assetCategories.map((tag) => tag._id));
    setCheckedTagIds([]);
  };

  const undo = () => {
    setTouchedTagIds([]);
    setCheckedTagIds([]);
  };

  const handleCancel = () => {
    undo();
    onCancel?.();
  };

  const handleSave = async () => {
    if (assets.length === 0) {
      return;
    }

    const add = touchedTagIds.filter((id) => checkedTagIds.includes(id));
    const remove = touchedTagIds.filter((id) => !checkedTagIds.includes(id));

    if (add.length === 0 && remove.length === 0) {
      onSaved?.();
      return;
    }

    await handleSaveTags(assets, { add, remove });
    onSaved?.();
  };

  /**
   * Side effects
   */

  useEffect(() => {
    setTouchedTagIds([]);
    setCheckedTagIds([]);
  }, [assets]);

  /**
   * Vars
   */

  const tagsAvailable = assetCategories.length > 0;

  const isDirty = touchedTagIds.length > 0;

  const tagStates = assetCategories.map((tag) => tagCheckState(tag._id));

  const allSelected: boolean | "indeterminate" = !tagsAvailable
    ? false
    : tagStates.every((state) => state === true)
      ? true
      : tagStates.every((state) => state === false)
        ? false
        : "indeterminate";

  /**
   * View
   */

  const NoTagsAvailable = () => {
    return (
      <span className="text-xs text-secondary-foreground/60">
        No tags available
      </span>
    );
  };

  const TagsList = () => {
    return (
      <div className="flex flex-col gap-2 overflow-y-auto flex-1">
        {assetCategories.map((tag) => {
          const checked = tagCheckState(tag._id);

          return (
            <div
              key={tag._id}
              id={`TagsEditor__tag-${tag._id}`}
              className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                id={`${editorId}-tag-${tag._id}`}
                className="TagsEditor__checkbox"
                name={tag._id}
                checked={checked}
                onCheckedChange={(checkedValue) =>
                  toggleTag(tag._id, checkedValue === true)
                }
              />
              <label
                htmlFor={`${editorId}-tag-${tag._id}`}
                className="text-xs text-secondary-foreground capitalize select-none">
                {tag.name}
              </label>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div id={editorId} className="flex flex-col gap-2 h-full overflow-hidden">
      {tagsAvailable ? <TagsList /> : <NoTagsAvailable />}
      {tagsAvailable && (
        <>
          <div className="border-t border-border/60" />
          <div className="flex items-center gap-2 cursor-pointer mb-1">
            <Checkbox
              id={`${editorId}-tag-all`}
              className="TagsEditor__checkbox"
              checked={allSelected}
              onCheckedChange={(checkedValue) =>
                checkedValue === true ? selectAll() : unselectAll()
              }
            />
            <label
              htmlFor={`${editorId}-tag-all`}
              className="text-xs font-medium text-secondary-foreground select-none">
              All
            </label>
          </div>
        </>
      )}
      {showActions && (
        <div className="flex flex-col gap-1.5">
          {/* {tagsAvailable && (
                        <div className='flex items-center gap-2 text-[11px] font-medium text-secondary-foreground'>
                            <button
                                type='button'
                                className='underline-offset-4 hover:underline cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:no-underline'
                                onClick={undo}
                                disabled={!isDirty}
                            >
                                Undo
                            </button>
                        </div>
                    )} */}
          {tagsAvailable && (
            <div className="flex items-center gap-2">
              <button
                id="TagsEditor__save"
                type="button"
                className="inline-flex h-8 flex-1 items-center justify-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 cursor-pointer disabled:opacity-50 disabled:cursor-default"
                onClick={handleSave}
                disabled={saving}>
                {saving ? "Saving…" : cta}
              </button>
              {onCancel && (
                <button
                  type="button"
                  className="inline-flex h-8 items-center justify-center rounded-md border bg-background px-3 text-xs font-medium text-secondary-foreground hover:bg-secondary cursor-pointer disabled:opacity-50 disabled:cursor-default"
                  onClick={handleCancel}
                  disabled={saving}>
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
